import { useState, useEffect, useCallback, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { MdShowChart } from "react-icons/md";
import { useCostAmounts } from "../../hooks/useCostsAmounts";
import HistoryDateFilter from "../common/HistoryDateFilter";

const InvoiceGraph = () => {
  const { getCostAmounts, loading, error } = useCostAmounts();
  const [chartData, setChartData] = useState([]);
  const [hasData, setHasData] = useState(false);
  const isMounted = useRef(true);

  const parseMonthDate = (dateStr) => {
    const [day, month, year] = dateStr.split('/');
    return {
      month: parseInt(month),
      year: parseInt(year)
    };
  };

  const fetchData = useCallback(async (startMonth, startYear, endMonth, endYear) => {
    const result = await getCostAmounts(startMonth, startYear, endMonth, endYear);
    if (isMounted.current && result.success && result.data) {
      const formatted = result.data.months.map((month, index) => ({
        month: month,
        amount: result.data.amounts[index],
        formattedAmount: `$${result.data.amounts[index].toFixed(2)}`,
      }));
      setChartData(formatted);
      setHasData(true);
    } else if (isMounted.current && !result.success) {
      setChartData([]);
      setHasData(false);
    }
  }, [getCostAmounts]);

  const handleRangeChange = useCallback((start, end) => {
    const startParsed = parseMonthDate(start);
    const endParsed = parseMonthDate(end);
    fetchData(startParsed.month, startParsed.year, endParsed.month, endParsed.year);
  }, [fetchData]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-1">{label}</p>
          <p className="text-primary-cyan text-lg font-bold">
            ${payload[0].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  const totalAmount = chartData.reduce((sum, item) => sum + item.amount, 0);

  if (loading && !hasData) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 ml-3">Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (error && !hasData) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="text-center py-12">
          <p className="text-red-400">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <MdShowChart className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">Cost Overview</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="text-lg sm:text-xl font-bold text-white">
            {chartData.length > 0 && `Total: $${totalAmount.toFixed(2)}`}
          </div>
          <HistoryDateFilter onRangeChange={handleRangeChange} />
        </div>
      </div>

      {chartData.length > 0 ? (
        <div className="w-full overflow-x-auto">
          <div className="min-w-[300px] sm:min-w-0" style={{ width: "100%", height: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF", fontSize: 10 }}
                  tickLine={{ stroke: "#374151" }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF", fontSize: 10 }}
                  tickLine={{ stroke: "#374151" }}
                  tickFormatter={(value) => `$${value}`}
                  width={50}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ color: "#9CA3AF", fontSize: "12px" }}
                  formatter={() => "Monthly Cost"}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  name="Cost"
                  stroke="#dc2626"
                  strokeWidth={3}
                  fill="url(#colorAmount)"
                  dot={{
                    fill: "#dc2626",
                    stroke: "#dc2626",
                    strokeWidth: 2,
                    r: 3,
                  }}
                  activeDot={{
                    fill: "#dc2626",
                    stroke: "#fff",
                    strokeWidth: 2,
                    r: 5,
                  }}
                  animationDuration={1000}
                  animationBegin={0}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-400">No data available for selected period</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceGraph;